import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FormFlowService from "../services/FormFlowService";
import AppointmentService from "../services/AppointmentService";
import TicketService from "../services/TicketService";

const INPUT_TYPE_OPTIONS = ["text", "email", "number", "phone", "password", "passcode"];
const FIELD_TYPE_OPTIONS = ["TextInput", "Dropdown"];
const OPTION_FIELD_TYPES = new Set(["Dropdown", "RadioButtonsGroup"]);
const SCREEN_KEY_REGEX = /^[A-Za-z_]+$/;
const FIELD_NAME_REGEX = /^\S+$/;
const MIN_DROPDOWN_OPTIONS = 1;
const MAX_DROPDOWN_OPTIONS = 200;
const MAX_OPTION_TITLE_LENGTH = 30;
const MAX_FIELD_LABEL_LENGTH = 30;

const makeUiKey = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const flowToken = (path) => `\${${path}}`;

const buildAutoPayloadFromFields = (fields = []) =>
  fields.reduce((acc, field) => {
    const key = String(field?.name || "").trim();
    if (!key) return acc;
    acc[key] = `\${form.${key}}`;
    return acc;
  }, {});

const makeScreen = (idx) => ({
  _uiKey: makeUiKey("screen"),
  screen_key: `SCREEN_${idx + 1}`,
  title: `Screen ${idx + 1}`,
  is_terminal: idx === 0,
  screen_index: idx,
  footer_label: idx === 0 ? "Complete" : "Next",
  action_type: null,
  payload_json: {},
  fields: [],
});

const makeDefaultOption = (idx) => ({
  id: `opt_${idx + 1}`,
  title: `Option ${idx + 1}`,
});

const makeField = (idx, type = "TextInput") => {
  const base = {
    _uiKey: makeUiKey("field"),
    id: null,
    name: `field_${idx + 1}`,
    label: `Field ${idx + 1}`,
    is_required: true,
    field_order: idx,
  };

  if (type === "Dropdown" || type === "RadioButtonsGroup") {
    return {
      ...base,
      type,
      placeholder: null,
      input_type: null,
      pattern: null,
      helper_text: "",
      options_json: [makeDefaultOption(0)],
    };
  }

  return {
    ...base,
    type: "TextInput",
    placeholder: "",
    input_type: "text",
    pattern: "",
    helper_text: "",
    options_json: null,
  };
};

const makeTextField = (idx) => makeField(idx, "TextInput");

function moveItem(arr, fromIdx, toIdx) {
  const copy = [...arr];
  const [item] = copy.splice(fromIdx, 1);
  copy.splice(toIdx, 0, item);
  return copy;
}
function parseOptionFieldFromFlow(child, idx) {
  const dataSource = Array.isArray(child["data-source"]) ? child["data-source"] : [];
  return {
    ...makeField(idx, child.type === "RadioButtonsGroup" ? "RadioButtonsGroup" : "Dropdown"),
    type: child.type === "RadioButtonsGroup" ? "RadioButtonsGroup" : "Dropdown",
    name: child.name || `field_${idx + 1}`,
    label: child.label || `Field ${idx + 1}`,
    is_required: Boolean(child.required),
    options_json: dataSource.map((opt, optIdx) => ({
      id: String(opt?.id ?? `opt_${optIdx + 1}`),
      title: String(opt?.title ?? `Option ${optIdx + 1}`),
    })),
  };
}

// function parseFlowJsonToDraft(flowJson) {
//   if (!flowJson || !Array.isArray(flowJson.screens)) throw new Error("Invalid flow JSON");
//   return flowJson.screens.map((screen, i) => {
//     const children = screen.layout?.children || [];
//     const footer = children.find((c) => c.type === "Footer") || null;
//     const fields = children
//       .filter((c) => ["TextInput", "Dropdown", "DatePicker"].includes(c.type)
//       )
//       .map((c, idx) => ({
//         ...makeTextField(idx, c.type),
//         name: c.name || `field_${idx + 1}`,
//         label: c.label || `Field ${idx + 1}`,
//         is_required: Boolean(c.required),
//         placeholder: c.placeholder ?? "",
//         input_type: c["input-type"] || "text",
//         pattern: c.pattern != null ? String(c.pattern) : "",
//         helper_text: c["helper-text"] ?? "",
//         min_date: c["min-date"] || "",
//         max_date: c["max-date"] || "",
//       }));

//     return {
//       _uiKey: makeUiKey("screen"),
//       screen_key: screen.id || `SCREEN_${i + 1}`,
//       title: screen.title || `Screen ${i + 1}`,
//       is_terminal: Boolean(screen.terminal),
//       screen_index: i,
//       footer_label: footer?.label || (screen.terminal ? "Complete" : "Next"),
//       action_type: footer?.["on-click-action"]?.name ?? null,
//       payload_json: footer?.["on-click-action"]?.payload ?? {},
//       fields,
//     };
//   });
// }
function parseFlowJsonToDraft(flowJson) {
  if (!flowJson || !Array.isArray(flowJson.screens)) throw new Error("Invalid flow JSON");

  return flowJson.screens.map((screen, i) => {
    const children = screen.layout?.children || [];
    const footer = children.find((c) => c.type === "Footer") || null;

    // ✅ ADD CalendarPicker to the filter
    const fields = children
      .filter((c) => c.type === "TextInput" || c.type === "Dropdown" || c.type === "RadioButtonsGroup")
      .map((c, idx) => {
        if (c.type === "TextInput") {
          return {
            ...makeTextField(idx),
            name: c.name || `field_${idx + 1}`,
            label: c.label || `Field ${idx + 1}`,
            is_required: Boolean(c.required),
            placeholder: c.placeholder ?? "",
            input_type: c["input-type"] || "text",
            pattern: c.pattern != null ? String(c.pattern) : "",
            helper_text: c["helper-text"] ?? "",
          };
        }
        return parseOptionFieldFromFlow(c, idx);
      });

    return {
      _uiKey: makeUiKey("screen"),
      screen_key: screen.id || `SCREEN_${i + 1}`,
      title: screen.title || `Screen ${i + 1}`,
      is_terminal: Boolean(screen.terminal),
      screen_index: i,
      footer_label: footer?.label || (screen.terminal ? "Complete" : "Next"),
      action_type: footer?.["on-click-action"]?.name ?? null,
      payload_json: footer?.["on-click-action"]?.payload ?? {},
      fields,
    };
  });
}

function getApiErrorMessage(error, fallback = "Something went wrong") {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.error?.error_user_msg ||
    error?.response?.data?.error?.error_user_title ||
    error?.message ||
    fallback
  );
}

function validateOptionField(field, screenKey) {
  if (!OPTION_FIELD_TYPES.has(field.type)) return null;

  const options = Array.isArray(field.options_json) ? field.options_json : [];
  if (options.length < MIN_DROPDOWN_OPTIONS) {
    return `Field "${field.name}" on ${screenKey} needs at least ${MIN_DROPDOWN_OPTIONS} option.`;
  }
  if (options.length > MAX_DROPDOWN_OPTIONS) {
    return `Field "${field.name}" on ${screenKey} supports at most ${MAX_DROPDOWN_OPTIONS} options.`;
  }
  if (String(field.label || "").trim().length > MAX_FIELD_LABEL_LENGTH) {
    return `Field label "${field.label}" on ${screenKey} must be ${MAX_FIELD_LABEL_LENGTH} characters or fewer.`;
  }

  const seenOptionIds = new Set();
  for (const [optIdx, opt] of options.entries()) {
    const optionId = String(opt?.id ?? "").trim();
    const optionTitle = String(opt?.title ?? "").trim();
    if (!optionId) {
      return `Option ${optIdx + 1} in field "${field.name}" on ${screenKey} is missing id.`;
    }
    if (!optionTitle) {
      return `Option ${optIdx + 1} in field "${field.name}" on ${screenKey} is missing title.`;
    }
    if (optionTitle.length > MAX_OPTION_TITLE_LENGTH) {
      return `Option title "${optionTitle}" in field "${field.name}" on ${screenKey} must be ${MAX_OPTION_TITLE_LENGTH} characters or fewer.`;
    }
    if (seenOptionIds.has(optionId)) {
      return `Duplicate option id "${optionId}" in field "${field.name}" on ${screenKey}.`;
    }
    seenOptionIds.add(optionId);
  }
  return null;
}

function validateBeforeSave(screens = []) {
  const seenFieldNames = new Set();
  for (const screen of screens || []) {
    if (!SCREEN_KEY_REGEX.test(String(screen.screen_key || ""))) {
      return `Invalid screen_key "${screen.screen_key}". Use only letters and underscore (_), no numbers or spaces.`;
    }
    for (const field of screen.fields || []) {
      const fieldName = String(field.name || "");
      if (!FIELD_NAME_REGEX.test(fieldName)) {
        return `Invalid field name "${fieldName}". Spaces are not allowed.`;
      }
      if (seenFieldNames.has(fieldName)) {
        return `Duplicate field name "${fieldName}". Field names must be unique in the flow.`;
      }
      seenFieldNames.add(fieldName);

      const optionError = validateOptionField(field, screen.screen_key);
      if (optionError) return optionError;
    }
  }
  return null;
}

function normalizeFieldForSave(field) {
  if (OPTION_FIELD_TYPES.has(field.type)) {
    return {
      ...field,
      placeholder: null,
      input_type: null,
      pattern: null,
      helper_text: field.helper_text && String(field.helper_text).trim() ? field.helper_text : null,
      options_json: (Array.isArray(field.options_json) ? field.options_json : []).map((opt, idx) => ({
        id: String(opt?.id ?? `opt_${idx + 1}`).trim(),
        title: String(opt?.title ?? "").trim(),
      })),
    };
  }

  return {
    ...field,
    options_json: null,
    pattern: field.pattern && String(field.pattern).trim() ? field.pattern : null,
    helper_text: field.helper_text && String(field.helper_text).trim() ? field.helper_text : null,
    placeholder: field.placeholder && String(field.placeholder).trim() ? field.placeholder : null,
  };
}

function FieldOptionsEditor({ options = [], onChange }) {
  const updateOption = (optIdx, patch) => {
    onChange(options.map((opt, idx) => (idx === optIdx ? { ...opt, ...patch } : opt)));
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Configure dropdown options (1–{MAX_DROPDOWN_OPTIONS}). Title max {MAX_OPTION_TITLE_LENGTH} chars. Submitted value uses option id.
      </Typography>
      {options.map((opt, optIdx) => (
        <Grid container spacing={1} key={`option-${optIdx}`} sx={{ mb: 1 }} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              label="Option id"
              fullWidth
              size="small"
              value={opt.id || ""}
              onChange={(e) => updateOption(optIdx, { id: e.target.value })}
              helperText="Unique value sent on submit"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Option title"
              fullWidth
              size="small"
              value={opt.title || ""}
              onChange={(e) => updateOption(optIdx, { title: e.target.value })}
              inputProps={{ maxLength: MAX_OPTION_TITLE_LENGTH }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <IconButton
              color="error"
              size="small"
              disabled={options.length <= MIN_DROPDOWN_OPTIONS}
              onClick={() => onChange(options.filter((_, idx) => idx !== optIdx))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button
        startIcon={<AddIcon />}
        size="small"
        variant="text"
        disabled={options.length >= MAX_DROPDOWN_OPTIONS}
        onClick={() => onChange([...options, makeDefaultOption(options.length)])}
      >
        Add option
      </Button>
    </Box>
  );
}

function PhoneMockPreview({ screen }) {
  const fields = screen.fields || [];
  const footerText = screen.footer_label || (screen.is_terminal ? "Complete" : "Next");

  const renderField = (field, idx) => {
    //  DROPDOWN preview
    if (field.type === "Dropdown") {
      let options = [];
      try {
        options = JSON.parse(field.options_json || "[]");
      } catch {
        options = [];
      }
      return (
        <FormControl fullWidth size="small" key={`${field.name}-${idx}`}>
          <InputLabel>{field.placeholder || field.label}</InputLabel>
          <Select
            value=""
            label={field.placeholder || field.label}
            readOnly
          >
            {options.map((opt, i) => (
              <MenuItem key={i} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (field.type === "DatePicker") {
      let unavailableDates = [];
      try {
        unavailableDates = JSON.parse(field.unavailable_dates_json || "[]");
      } catch {
        unavailableDates = [];
      }

      return (
        <TextField
          key={`${field.name}-${idx}`}
          type="date"
          size="small"
          fullWidth
          value=""
          placeholder={field.placeholder || field.label}
          InputProps={{ readOnly: true, style: { fontSize: 13 } }}
          required={Boolean(field.is_required)}
        // label={field.label}
        />
      );
    }
    if (field.type === "CalendarPicker") {
      return (
        <TextField
          key={`${field.name}-${idx}`}
          size="small"
          fullWidth
          value=""
          placeholder={field.placeholder || field.label}
          InputProps={{ readOnly: true, style: { fontSize: 13 } }}
          required={Boolean(field.is_required)}
          InputLabelProps={{ shrink: true }}
        />
      );
    }
    // TextInput preview 
    return (
      <TextField
        key={`${field.name}-${idx}`}
        size="small"
        fullWidth
        value=""
        placeholder={field.placeholder || field.label}
        InputProps={{ readOnly: true, style: { fontSize: 13 } }}
        required={Boolean(field.is_required)}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 2, bgcolor: "#f3f4f6", borderRadius: 2 }}>
      <Box sx={{ width: 310, height: 520, bgcolor: "#ffffff", borderRadius: 3, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.06)" }}>
        <Box sx={{ height: 52, bgcolor: "#f5f5f5", px: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 22, height: 22, borderRadius: 999, bgcolor: "#0f172a", opacity: 0.85, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ color: "#fff", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>×</Box>
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{screen.title}</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ color: "#64748b" }}>⋮</Typography>
        </Box>

        <Box sx={{ p: 2, pt: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 1 }}>{screen.title}</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
            {fields.map((f, idx) => {
              if (OPTION_FIELD_TYPES.has(f.type)) {
                const options = Array.isArray(f.options_json) ? f.options_json : [];
                return (
                  <FormControl key={`${f.name}-${idx}`} fullWidth size="small" required={Boolean(f.is_required)}>
                    <InputLabel>{f.label || f.name}</InputLabel>
                    <Select label={f.label || f.name} value="" displayEmpty>
                      <MenuItem value="" disabled>
                        <em>{options[0]?.title || "Select an option"}</em>
                      </MenuItem>
                      {options.map((opt) => (
                        <MenuItem key={String(opt.id)} value={String(opt.id)}>
                          {opt.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }

              return (
                <TextField
                  key={`${f.name}-${idx}`}
                  size="small"
                  fullWidth
                  value={""}
                  label={f.label}
                  placeholder={f.placeholder || f.label}
                  InputProps={{ readOnly: true, style: { fontSize: 13 } }}
                  required={Boolean(f.is_required)}
                />
              );
            })}
          </Box>
          <Button
            disabled
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              borderRadius: 4,
              bgcolor: "#e5e7eb",
              color: "#374151",
              boxShadow: "none"
            }}
          >
            {footerText}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
            Managed by the business
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function FormFlowBuilder({ open, onClose, initialFormId = null, onSaved }) {
  const [formId, setFormId] = useState(initialFormId);
  const [name, setName] = useState("");
  const [type, setType] = useState("address");
  const [screens, setScreens] = useState([makeScreen(0)]);
  const [preview, setPreview] = useState(null);
  const [flowJsonText, setFlowJsonText] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [payloadEditors, setPayloadEditors] = useState({});
  const [payloadManualEdit, setPayloadManualEdit] = useState({});
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [appointmentResources, setAppointmentResources] = useState([]);
  const [ticketEvents, setTicketEvents] = useState([]);
  const [ticketTiersByEvent, setTicketTiersByEvent] = useState({});
  const [nodeDraft, setNodeDraft] = useState({
    appointment_type_id: "",
    resource_id: "",
    ticket_event_id: "",
    ticket_tier_id: "",
    qty: 1,
  });
  const [previewScreenIdx, setPreviewScreenIdx] = useState(0);
  const [newUnavailableDate, setNewUnavailableDate] = useState("");
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [newOptionValue, setNewOptionValue] = useState("");


  useEffect(() => {
    setFormId(initialFormId || null);
    setPreview(null);
    setFlowJsonText("");
    setPreviewScreenIdx(0);

    if (!initialFormId) {
      setName("");
      setType("address");
      const initialScreen = makeScreen(0);
      setScreens([initialScreen]);
      setPayloadEditors({ 0: JSON.stringify(buildAutoPayloadFromFields(initialScreen.fields || []), null, 2) });
      setPayloadManualEdit({ 0: false });
      return;
    }

    FormFlowService.getForm(initialFormId)
      .then((res) => {
        setFormId(res.form.id);
        setName(res.form.name || "");
        setType(res.form.type || "address");
        setPreview(res.preview || null);
        setScreens(
          (res.screens || []).map((s, idx) => ({
            _uiKey: makeUiKey("screen"),
            ...s,
            screen_index: idx,
            fields: (s.fields || []).map((f, fIdx) => {
              const fieldType = f.type || "TextInput";
              const baseField = OPTION_FIELD_TYPES.has(fieldType)
                ? makeField(fIdx, fieldType === "RadioButtonsGroup" ? "RadioButtonsGroup" : "Dropdown")
                : makeTextField(fIdx);
              return {
                _uiKey: makeUiKey("field"),
                ...baseField,
                ...f,
                type: fieldType,
                pattern: f.pattern || "",
                helper_text: f.helper_text || "",
                placeholder: f.placeholder || "",
                options_json: Array.isArray(f.options_json)
                  ? f.options_json.map((opt, optIdx) => ({
                    id: String(opt?.id ?? `opt_${optIdx + 1}`),
                    title: String(opt?.title ?? `Option ${optIdx + 1}`),
                  }))
                  : baseField.options_json,
              };
            }),
          })),
        );

        const payloadState = {};
        const manualState = {};
        (res.screens || []).forEach((s, idx) => {
          const payloadObj = s.payload_json && Object.keys(s.payload_json).length > 0
            ? s.payload_json
            : buildAutoPayloadFromFields(s.fields || []);
          payloadState[idx] = JSON.stringify(payloadObj, null, 2);
          manualState[idx] = false;
        });
        setPayloadEditors(payloadState);
        setPayloadManualEdit(manualState);
      })
      .catch((err) => setSnackbar({ open: true, message: getApiErrorMessage(err, "Failed to load form"), severity: "error" }));
  }, [initialFormId]);

  const payloadScreens = useMemo(
    () =>
      screens.map((s, sIdx) => ({
        ...s,
        screen_index: sIdx,
        fields: (s.fields || []).map((f, fIdx) => ({
          ...f,
          field_order: fIdx,
        })),
      })),
    [screens],
  );

  useEffect(() => {
    if (!payloadScreens.length) return;
    if (previewScreenIdx > payloadScreens.length - 1) setPreviewScreenIdx(0);
  }, [payloadScreens, previewScreenIdx]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [types, resources, events] = await Promise.all([
          AppointmentService.listTypes().catch(() => []),
          AppointmentService.listResources().catch(() => []),
          TicketService.listEvents().catch(() => []),
        ]);
        setAppointmentTypes(Array.isArray(types) ? types : []);
        setAppointmentResources(Array.isArray(resources) ? resources : []);
        const eventRows = Array.isArray(events) ? events : [];
        setTicketEvents(eventRows);
        const tierPairs = await Promise.all(
          eventRows.map(async (event) => [String(event.id), await TicketService.listTiers(event.id).catch(() => [])])
        );
        setTicketTiersByEvent(Object.fromEntries(tierPairs));
      } catch (_err) {
        setAppointmentTypes([]);
        setAppointmentResources([]);
        setTicketEvents([]);
        setTicketTiersByEvent({});
      }
    })();
  }, [open]);

  const selectedPreviewScreen = payloadScreens[previewScreenIdx] || payloadScreens[0] || null;

  const saveDraft = async () => {
    setLoading(true);
    try {
      const validationError = validateBeforeSave(payloadScreens);
      if (validationError) {
        setSnackbar({ open: true, message: validationError, severity: "error" });
        return;
      }
      const payload = {
        name,
        type,
        screens: payloadScreens.map((s) => ({
          screen_key: s.screen_key,
          title: s.title,
          is_terminal: Boolean(s.is_terminal),
          screen_index: s.screen_index,
          footer_label: s.footer_label ?? null,
          action_type: s.action_type ?? null,
          payload_json: s.payload_json ?? {},
          fields: (s.fields || []).map((f, idx) => normalizeFieldForSave({
            ...f,
            field_order: idx,
          })),
        })),
      };
      const result = formId ? await FormFlowService.updateDraft(formId, payload) : await FormFlowService.createDraft(payload);
      if (result?.formId) setFormId(result.formId);
      setPreview(result?.preview || null);
      onSaved?.();
      setSnackbar({ open: true, message: "Draft saved", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: getApiErrorMessage(err, "Save failed"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const updateItemInArray = (sIdx, fIdx, field, index, updatedItem, jsonFieldName = 'options_json') => {
    const currentArray = getArrayFromField(field, jsonFieldName);
    const newArray = [...currentArray];
    newArray[index] = { ...newArray[index], ...updatedItem };
    updateArrayInField(sIdx, fIdx, newArray, jsonFieldName);
  };

  const getArrayFromField = (field, jsonFieldName = 'options_json') => {
    try {
      const parsed = JSON.parse(field[jsonFieldName] || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const updateArrayInField = (sIdx, fIdx, newArray, jsonFieldName = 'options_json') => {
    updateField(sIdx, fIdx, {
      [jsonFieldName]: JSON.stringify(newArray)
    });
  };

  const addItemToArray = (sIdx, fIdx, field, newItem,
    jsonFieldName = 'options_json',
    duplicateCheckKey = null,
    duplicateMessage = "Item already exists") => {
    if (!newItem) return false;

    const currentArray = getArrayFromField(field, jsonFieldName);

    if (duplicateCheckKey && currentArray.some(item => item[duplicateCheckKey] === newItem[duplicateCheckKey])) {
      setSnackbar({ open: true, message: duplicateMessage, severity: "error" });
      return false;
    }
    if (!duplicateCheckKey && currentArray.includes(newItem)) {
      setSnackbar({ open: true, message: duplicateMessage, severity: "error" });
      return false;
    }
    const newArray = [...currentArray, newItem];
    updateArrayInField(sIdx, fIdx, newArray, jsonFieldName);
    return true;
  };


  const removeItemFromArray = (sIdx, fIdx, field, indexToRemove, jsonFieldName = 'options_json') => {
    const currentArray = getArrayFromField(field, jsonFieldName);
    const newArray = currentArray.filter((_, i) => i !== indexToRemove);
    updateArrayInField(sIdx, fIdx, newArray, jsonFieldName);
  };


  const clearArray = (sIdx, fIdx, field, jsonFieldName = 'options_json', itemName = "items") => {
    const currentArray = getArrayFromField(field, jsonFieldName);
    if (currentArray.length === 0) return;
    updateArrayInField(sIdx, fIdx, [], jsonFieldName);
    setSnackbar({ open: true, message: `All ${itemName} cleared`, severity: "success" });
  };

  const updateField = (sIdx, fIdx, patch) => {
    setScreens((prev) => {
      const next = prev.map((s, i) =>
        i === sIdx ? { ...s, fields: s.fields.map((f, fi) => (fi === fIdx ? { ...f, ...patch } : f)) } : s,
      );
      const screen = next[sIdx];
      if (screen && !payloadManualEdit[sIdx]) {
        const autoPayload = buildAutoPayloadFromFields(screen.fields || []);
        screen.payload_json = autoPayload;
        setPayloadEditors((prevEditors) => ({
          ...prevEditors,
          [sIdx]: JSON.stringify(autoPayload, null, 2),
        }));
      }
      return [...next];
    });
  };

  const updateScreen = (sIdx, patch) => {
    setScreens((prev) => prev.map((s, i) => (i === sIdx ? { ...s, ...patch } : s)));
  };

  const applyNodePayload = (sIdx, actionType, payload) => {
    updateScreen(sIdx, { action_type: actionType, payload_json: payload });
    setPayloadEditors((prev) => ({ ...prev, [sIdx]: JSON.stringify(payload, null, 2) }));
    setPayloadManualEdit((prev) => ({ ...prev, [sIdx]: true }));
  };

  const onReorderField = (sIdx, fromIdx, toIdx) => {
    setScreens((prev) =>
      prev.map((s, i) => {
        if (i !== sIdx) return s;
        const moved = moveItem(s.fields || [], fromIdx, toIdx).map((ff, idx) => ({ ...ff, field_order: idx }));
        return { ...s, fields: moved };
      }),
    );
  };

  if (!open) return null;

  return (
    <Box sx={{ p: 2, height: "calc(85vh - 24px)" }}>
      <Grid container spacing={2} sx={{ height: "100%", overflow: "hidden", flexWrap: "nowrap" }}>
        <Grid item xs={12} md={7} sx={{ height: "100%", overflow: "auto", pr: 1, minHeight: 0, minWidth: 0 }}>
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                Form Flow Builder
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure screens and fields. Preview updates instantly while you edit.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Flow name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
                      <MenuItem value="address">Address</MenuItem>
                      <MenuItem value="appointment">Appointment</MenuItem>
                      <MenuItem value="ticket">Ticket / reservation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {screens.map((screen, sIdx) => (
            <Card key={screen._uiKey || `screen-${sIdx}`} sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="screen_key"
                      fullWidth
                      value={screen.screen_key}
                      onChange={(e) => updateScreen(sIdx, { screen_key: e.target.value })}
                      helperText="Only letters and underscore (_), no numbers/spaces"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="title" fullWidth value={screen.title} onChange={(e) => updateScreen(sIdx, { title: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={<Checkbox checked={Boolean(screen.is_terminal)} onChange={(e) => updateScreen(sIdx, { is_terminal: e.target.checked })} />}
                      label="Terminal"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Footer Action
                </Typography>
                <Grid container spacing={1.2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Footer button label"
                      fullWidth
                      value={screen.footer_label || ""}
                      onChange={(e) => updateScreen(sIdx, { footer_label: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} width={"150px"}>
                    <FormControl fullWidth>
                      <InputLabel>Action</InputLabel>
                      <Select label="Action" value={screen.action_type || ""} onChange={(e) => updateScreen(sIdx, { action_type: e.target.value || null })}>
                        <MenuItem value="">Auto</MenuItem>
                        <MenuItem value="complete">complete</MenuItem>
                        <MenuItem value="navigate">navigate</MenuItem>
                        <MenuItem value="data_exchange">data_exchange</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <div>
                  <Grid item xs={12}>
                    <TextField
                      label="Footer payload JSON"
                      multiline
                      minRows={3}
                      fullWidth
                      value={payloadEditors[sIdx] ?? JSON.stringify(screen.payload_json || {}, null, 2)}
                      onChange={(e) => {
                        setPayloadEditors((prev) => ({ ...prev, [sIdx]: e.target.value }));
                        setPayloadManualEdit((prev) => ({ ...prev, [sIdx]: true }));
                      }}
                      helperText='For navigate action, payload can include: { "screen": "SCREEN_2" }'
                    />
                  </Grid>
                  {/* <Grid item xs={12} sm={4}> */}
                    <Button
                      sx={{ mt: { xs: 0, sm: 1 } }}
                      variant="outlined"
                      onClick={() => {
                        try {
                          const parsed = JSON.parse(payloadEditors[sIdx] || "{}");
                          updateScreen(sIdx, { payload_json: parsed });
                          setPayloadManualEdit((prev) => ({ ...prev, [sIdx]: true }));
                          setSnackbar({ open: true, message: "Footer payload applied", severity: "success" });
                        } catch {
                          setSnackbar({ open: true, message: "Invalid footer payload JSON", severity: "error" });
                        }
                      }}
                    >
                      Apply Payload JSON
                    </Button>
                  {/* </Grid> */}
                  </div>
                  {(type === "appointment" || type === "ticket") && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: "action.hover" }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Booking action dropdowns
                          </Typography>
                          {type === "appointment" ? (
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Appointment service</InputLabel>
                                  <Select label="Appointment service" value={nodeDraft.appointment_type_id} onChange={(e) => setNodeDraft((p) => ({ ...p, appointment_type_id: e.target.value }))}>
                                    {appointmentTypes.map((row) => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Resource</InputLabel>
                                  <Select label="Resource" value={nodeDraft.resource_id} onChange={(e) => setNodeDraft((p) => ({ ...p, resource_id: e.target.value }))}>
                                    <MenuItem value="">Any mapped resource</MenuItem>
                                    {appointmentResources.map((row) => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => applyNodePayload(sIdx, "data_exchange", {
                                    vertical_action: "appointment.availability",
                                    appointment_type_id: Number(nodeDraft.appointment_type_id || 0) || flowToken("form.appointment_type_id"),
                                    resource_id: nodeDraft.resource_id ? Number(nodeDraft.resource_id) : null,
                                    range_start_utc: flowToken("system.now"),
                                    range_end_utc: flowToken("system.now_plus_7d"),
                                  })}
                                >
                                  Use availability payload
                                </Button>
                              </Grid>
                            </Grid>
                          ) : (
                            <Grid container spacing={1}>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Event / route</InputLabel>
                                  <Select label="Event / route" value={nodeDraft.ticket_event_id} onChange={(e) => setNodeDraft((p) => ({ ...p, ticket_event_id: e.target.value, ticket_tier_id: "" }))}>
                                    {ticketEvents.map((row) => <MenuItem key={row.id} value={row.id}>{row.title}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Tier</InputLabel>
                                  <Select label="Tier" value={nodeDraft.ticket_tier_id} onChange={(e) => setNodeDraft((p) => ({ ...p, ticket_tier_id: e.target.value }))}>
                                    {(ticketTiersByEvent[String(nodeDraft.ticket_event_id)] || []).map((row) => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => applyNodePayload(sIdx, "data_exchange", {
                                    vertical_action: "ticket.hold.create",
                                    contact_id: flowToken("contact.id"),
                                    lines: [{
                                      ticket_tier_id: Number(nodeDraft.ticket_tier_id || 0) || flowToken("form.ticket_tier_id"),
                                      qty: Number(nodeDraft.qty || 1),
                                    }],
                                    idempotency_key: `${flowToken("conversation.id")}:ticket_hold`,
                                  })}
                                >
                                  Use ticket hold payload
                                </Button>
                              </Grid>
                            </Grid>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Fields (drag to reorder)
                </Typography>

                {(screen.fields || []).map((field, fIdx) => (
                  <Card
                    key={field._uiKey || `field-${fIdx}`}
                    variant="outlined"
                    sx={{ mb: 1, borderRadius: 2, cursor: "grab" }}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", String(fIdx))}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const fromIdx = Number(e.dataTransfer.getData("text/plain"));
                      if (Number.isNaN(fromIdx) || fromIdx === fIdx) return;
                      onReorderField(sIdx, fromIdx, fIdx);
                    }}
                  >
                    <CardContent sx={{ pb: "12px !important" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Field {fIdx + 1}
                        </Typography>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => {
                            setScreens((prev) =>
                              prev.map((s, i) => {
                                if (i !== sIdx) return s;
                                const nextFields = s.fields.filter((_, fi) => fi !== fIdx).map((ff, idx) => ({ ...ff, field_order: idx }));
                                if (!payloadManualEdit[sIdx]) {
                                  const autoPayload = buildAutoPayloadFromFields(nextFields);
                                  setPayloadEditors((prevEditors) => ({
                                    ...prevEditors,
                                    [sIdx]: JSON.stringify(autoPayload, null, 2),
                                  }));
                                  return { ...s, fields: nextFields, payload_json: autoPayload };
                                }
                                return { ...s, fields: nextFields };
                              }),
                            );
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <FormControlLabel
                        sx={{ mb: 1 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(field.is_required)}
                            onChange={(e) => updateField(sIdx, fIdx, { is_required: e.target.checked })}
                          />
                        }
                        label="Required"
                      />

                      <Grid container spacing={1.2}>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>Field type</InputLabel>
                            <Select
                              label="Field type"
                              value={OPTION_FIELD_TYPES.has(field.type) ? "Dropdown" : "TextInput"}
                              onChange={(e) => {
                                const nextType = e.target.value;
                                if (nextType === "Dropdown") {
                                  updateField(sIdx, fIdx, {
                                    type: "Dropdown",
                                    placeholder: null,
                                    input_type: null,
                                    pattern: null,
                                    options_json: Array.isArray(field.options_json) && field.options_json.length
                                      ? field.options_json
                                      : [makeDefaultOption(0)],
                                  });
                                  return;
                                }
                                updateField(sIdx, fIdx, {
                                  type: "TextInput",
                                  placeholder: field.placeholder || "",
                                  input_type: field.input_type || "text",
                                  pattern: field.pattern || "",
                                  options_json: null,
                                });
                              }}
                            >
                              {FIELD_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="name"
                            fullWidth
                            value={field.name}
                            onChange={(e) => updateField(sIdx, fIdx, { name: e.target.value })}
                            helperText="Must be unique across flow. No spaces."
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="label"
                            fullWidth
                            value={field.label}
                            onChange={(e) => updateField(sIdx, fIdx, { label: e.target.value })}
                            inputProps={{ maxLength: MAX_FIELD_LABEL_LENGTH }}
                            helperText={OPTION_FIELD_TYPES.has(field.type) ? `Max ${MAX_FIELD_LABEL_LENGTH} characters` : undefined}
                          />
                        </Grid>
                        {field.type === "TextInput" ? (
                          <>
                            <Grid item xs={12} sm={4}>
                              <FormControl fullWidth>
                                <InputLabel>input_type</InputLabel>
                                <Select
                                  label="input_type"
                                  value={field.input_type || "text"}
                                  onChange={(e) => updateField(sIdx, fIdx, { input_type: e.target.value })}
                                >
                                  {INPUT_TYPE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                      {opt}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField label="pattern" fullWidth value={field.pattern || ""} onChange={(e) => updateField(sIdx, fIdx, { pattern: e.target.value })} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField label="helper_text" fullWidth value={field.helper_text || ""} onChange={(e) => updateField(sIdx, fIdx, { helper_text: e.target.value })} />
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={12}>
                            <FieldOptionsEditor
                              options={Array.isArray(field.options_json) ? field.options_json : []}
                              onChange={(nextOptions) => updateField(sIdx, fIdx, { options_json: nextOptions })}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setScreens((prev) =>
                        prev.map((s, i) => {
                          if (i !== sIdx) return s;
                          const nextFields = [...(s.fields || []), makeTextField((s.fields || []).length)];
                          if (!payloadManualEdit[sIdx]) {
                            const autoPayload = buildAutoPayloadFromFields(nextFields);
                            setPayloadEditors((prevEditors) => ({
                              ...prevEditors,
                              [sIdx]: JSON.stringify(autoPayload, null, 2),
                            }));
                            return { ...s, fields: nextFields, payload_json: autoPayload };
                          }
                          return { ...s, fields: nextFields };
                        }),
                      )
                    }
                  >
                    Add TextInput
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setScreens((prev) =>
                        prev.map((s, i) => {
                          if (i !== sIdx) return s;
                          const nextFields = [...(s.fields || []), makeField((s.fields || []).length, "Dropdown")];
                          if (!payloadManualEdit[sIdx]) {
                            const autoPayload = buildAutoPayloadFromFields(nextFields);
                            setPayloadEditors((prevEditors) => ({
                              ...prevEditors,
                              [sIdx]: JSON.stringify(autoPayload, null, 2),
                            }));
                            return { ...s, fields: nextFields, payload_json: autoPayload };
                          }
                          return { ...s, fields: nextFields };
                        }),
                      )
                    }
                  >
                    Add Dropdown
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                const newScreen = makeScreen(screens.length);
                setScreens((prev) => [...prev, newScreen]);
                setPayloadEditors((prev) => ({
                  ...prev,
                  [screens.length]: JSON.stringify(buildAutoPayloadFromFields(newScreen.fields || []), null, 2),
                }));
                setPayloadManualEdit((prev) => ({ ...prev, [screens.length]: false }));
              }}
            >
              Add Screen
            </Button>
          </Box>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Paste Meta flow_json (optional)
              </Typography>
              <TextField multiline minRows={5} fullWidth value={flowJsonText} onChange={(e) => setFlowJsonText(e.target.value)} />
              <Button
                sx={{ mt: 1 }}
                variant="outlined"
                onClick={() => {
                  try {
                    const parsedScreens = parseFlowJsonToDraft(JSON.parse(flowJsonText));
                    setScreens(parsedScreens);
                    const payloadState = {};
                    const manualState = {};
                    parsedScreens.forEach((s, idx) => {
                      const payloadObj = s.payload_json && Object.keys(s.payload_json).length > 0
                        ? s.payload_json
                        : buildAutoPayloadFromFields(s.fields || []);
                      payloadState[idx] = JSON.stringify(payloadObj, null, 2);
                      manualState[idx] = false;
                    });
                    setPayloadEditors(payloadState);
                    setPayloadManualEdit(manualState);
                    setSnackbar({ open: true, message: "Flow JSON parsed", severity: "success" });
                  } catch {
                    setSnackbar({ open: true, message: "Invalid JSON", severity: "error" });
                  }
                }}
              >
                Parse JSON
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5} sx={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column", gap: 2, minHeight: 0, minWidth: 0 }}>
          <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Preview
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <FormControl size="small">
                    <InputLabel>Screen</InputLabel>
                    <Select
                      label="Screen"
                      value={previewScreenIdx}
                      onChange={(e) => setPreviewScreenIdx(Number(e.target.value))}
                    >
                      {payloadScreens.map((s, idx) => (
                        <MenuItem key={`${s.screen_key}-${idx}`} value={idx}>
                          {s.screen_key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {selectedPreviewScreen ? (
                  <>
                    <Box sx={{ mt: 1 }}>
                      <PhoneMockPreview screen={selectedPreviewScreen} />
                    </Box>

                    {preview?.preview_url ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                          Meta Preview
                        </Typography>
                        <iframe
                          title="Meta Flow Preview"
                          src={preview.preview_url}
                          style={{ width: "100%", height: 260, border: "1px solid #e5e7eb", borderRadius: 8 }}
                        />
                      </Box>
                    ) : null}
                  </>
                ) : (
                  <Typography color="text.secondary">Add screens to see preview.</Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button variant="contained" onClick={saveDraft} disabled={loading}>
                  {loading ? "Saving..." : "Save Draft"}
                </Button>
                <Button
                  variant="outlined"
                  disabled={!formId || loading}
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const r = await FormFlowService.publish(formId);
                      setPreview(r?.preview || null);
                      setSnackbar({ open: true, message: "Flow published", severity: "success" });
                      onSaved?.();
                    } catch (err) {
                      setSnackbar({ open: true, message: getApiErrorMessage(err, "Publish failed"), severity: "error" });
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Publish
                </Button>
                <Button
                  variant="outlined"
                  disabled={!formId || loading}
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const r = await FormFlowService.revertToDraft(formId);
                      setPreview(r?.preview || null);
                      setSnackbar({ open: true, message: "Reverted to draft", severity: "success" });
                      onSaved?.();
                    } catch (err) {
                      setSnackbar({ open: true, message: getApiErrorMessage(err, "Revert failed"), severity: "error" });
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Revert to Draft
                </Button>
                <Button
                  variant="text"
                  color="error"
                  disabled={!formId || loading}
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await FormFlowService.deleteForm(formId);
                      setSnackbar({ open: true, message: "Flow deleted", severity: "success" });
                      onSaved?.();
                      onClose?.();
                    } catch (err) {
                      setSnackbar({ open: true, message: getApiErrorMessage(err, "Delete failed"), severity: "error" });
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Delete
                </Button>
                <Button variant="text" onClick={onClose} disabled={loading}>
                  Close
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
